<template>
  <div class="booking-orders-public-page">
    <!-- Header & Logo Section -->
    <header class="booking-header">
      <div class="container header-container">
        <div class="brand-logo" @click="handleGoHome">
          <div class="logo-icon">
            <i class="fas fa-ticket-alt" />
          </div>
          <div class="brand-text">
            <h1 class="brand-title">TDM BOOKING</h1>
            <span class="brand-subtitle">Đơn hàng & Vé của tôi</span>
          </div>
        </div>

        <nav class="header-nav">
          <el-button
            type="text"
            icon="el-icon-place"
            class="nav-link"
            @click="handleGoHome"
          >
            Khám phá điểm đến
          </el-button>
          <el-button
            type="primary"
            round
            icon="el-icon-tickets"
            class="my-orders-btn active"
            @click="handleGoMyOrders"
          >
            Đơn hàng của tôi
          </el-button>
        </nav>
      </div>
    </header>

    <!-- Main Order List Content -->
    <main class="container main-content">
      <div class="page-title-wrap mb-4 mt-5">
        <div class="title-flex">
          <h2 class="page-title"><i class="fas fa-history text-primary mr-2" /> Danh Sách Đơn Hàng Đã Đặt</h2>
          <el-button icon="el-icon-refresh" size="small" circle title="Làm mới" @click="loadOrders" />
        </div>
        <p class="page-desc">
          Quản lý, tiếp tục thanh toán đơn hàng đang chờ hoặc xem lại vé điện tử của bạn
        </p>
      </div>

      <!-- Search & Filter bar -->
      <div class="filter-card mb-4">
        <el-row :gutter="16">
          <el-col :xs="24" :sm="14" :md="12">
            <el-input
              v-model="searchKey"
              placeholder="Tìm theo mã đơn hàng hoặc số điện thoại..."
              prefix-icon="el-icon-search"
              clearable
            />
          </el-col>
          <el-col :xs="24" :sm="10" :md="12" class="text-right">
            <span class="total-badge">
              Tổng số: <strong>{{ filteredOrders.length }}</strong> đơn hàng
            </span>
          </el-col>
        </el-row>
      </div>

      <!-- Empty Orders State -->
      <el-empty
        v-if="!filteredOrders.length"
        description="Bạn chưa có đơn đặt vé nào được lưu trên thiết bị này."
      >
        <el-button type="primary" icon="el-icon-date" @click="handleGoHome">
          Khám phá và đặt vé ngay
        </el-button>
      </el-empty>

      <!-- Orders List Cards -->
      <div v-else class="orders-list-wrap">
        <el-row :gutter="20">
          <el-col
            v-for="order in filteredOrders"
            :key="order.id || order.code"
            :xs="24"
            :sm="24"
            :md="12"
            class="mb-4"
          >
            <el-card class="order-card" shadow="hover">
              <div class="order-card-header">
                <div class="order-code">
                  <i class="fas fa-receipt mr-1 text-primary" />
                  <strong>{{ order.code || order.id }}</strong>
                </div>
                <el-tag :type="getStatusTagType(order.status)" size="small" effect="dark">
                  {{ getStatusText(order.status) }}
                </el-tag>
              </div>

              <div class="order-card-body">
                <div class="place-preview-flex">
                  <img :src="getOrderImage(order)" :alt="order.place_name" class="place-thumb" />
                  <div class="place-info">
                    <h4 class="place-name text-truncate" :title="order.place_name">
                      {{ order.place_name }}
                    </h4>
                    <p class="order-date"><i class="far fa-calendar-alt mr-1" /> Ngày tham quan: <strong>{{ formatDate(order.booking_date) }}</strong></p>
                    <p class="order-time"><i class="far fa-clock mr-1" /> Tạo lúc: {{ order.created_at }}</p>
                    <p v-if="order.status === 'PENDING'" class="text-warning font-weight-bold font-size-xs mt-1">
                      ⌛ Tạm giữ 15 phút (Còn: {{ getRemainingMinutes(order) }})
                    </p>
                  </div>
                </div>

                <div class="order-details-summary mt-3 pt-3 border-top">
                  <el-row :gutter="12">
                    <el-col :span="12">
                      <span class="detail-label">Người Đại Diện:</span>
                      <div class="detail-val font-weight-bold">{{ getCustomerName(order) }}</div>
                    </el-col>
                    <el-col :span="12">
                      <span class="detail-label">Số Điện Thoại:</span>
                      <div class="detail-val">{{ getCustomerPhone(order) }}</div>
                    </el-col>
                  </el-row>

                  <el-row :gutter="12" class="mt-2">
                    <el-col :span="12">
                      <span class="detail-label">Số Lượng Vé:</span>
                      <div class="detail-val">{{ order.quantity }} vé</div>
                    </el-col>
                    <el-col :span="12">
                      <span class="detail-label">Tổng Thanh Toán:</span>
                      <div class="detail-val text-danger font-weight-bold">
                        {{ formatCurrency(order.total_price) }}
                      </div>
                    </el-col>
                  </el-row>
                </div>
              </div>

              <div class="order-card-footer">
                <span class="pay-method">
                  <i class="fas fa-credit-card mr-1" /> {{ getPaymentMethodText(order.payment_method) }}
                </span>
                <div class="footer-btns">
                  <!-- Actions for PENDING status -->
                  <template v-if="order.status === 'PENDING'">
                    <el-button
                      size="small"
                      type="warning"
                      icon="el-icon-right"
                      @click="handleResumeBooking(order)"
                    >
                      Tiếp tục đặt vé
                    </el-button>
                    <el-button
                      size="small"
                      type="danger"
                      plain
                      icon="el-icon-close"
                      @click="handleCancelOrder(order)"
                    >
                      Hủy
                    </el-button>
                  </template>

                  <!-- Actions for PAID status -->
                  <template v-else-if="order.status === 'PAID'">
                    <el-button size="small" type="primary" plain icon="el-icon-view" @click="handleOpenDetail(order)">
                      Xem chi tiết vé
                    </el-button>
                  </template>

                  <!-- Actions for CANCELLED status -->
                  <template v-else>
                    <el-tag type="info" size="small">Đã hủy tự động</el-tag>
                  </template>
                </div>
              </div>
            </el-card>
          </el-col>
        </el-row>
      </div>
    </main>

    <!-- Order Detail Dialog -->
    <el-dialog
      title="Chi Tiết Đơn Hàng & Vé Điện Tử"
      :visible.sync="dialogVisible"
      width="650px"
      append-to-body
      custom-class="order-detail-dialog"
    >
      <div v-if="selectedOrder" class="dialog-receipt-wrap">
        <!-- E-Ticket Banner -->
        <div class="ticket-header-box">
          <div class="ticket-brand">TDM BOOKING E-TICKET</div>
          <div class="ticket-code">{{ selectedOrder.code }}</div>
        </div>

        <div class="ticket-body p-4">
          <h4 class="font-weight-bold text-dark mb-3">{{ selectedOrder.place_name }}</h4>

          <el-descriptions :column="2" border size="small" class="mb-3">
            <el-descriptions-item label="Trạng thái">
              <el-tag :type="getStatusTagType(selectedOrder.status)" size="small">
                {{ getStatusText(selectedOrder.status) }}
              </el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="Ngày tham quan">
              <strong>{{ formatDate(selectedOrder.booking_date) }}</strong>
            </el-descriptions-item>
            <el-descriptions-item label="Tổng số vé">
              <strong>{{ selectedOrder.quantity }} vé</strong>
            </el-descriptions-item>
            <el-descriptions-item label="Người đại diện">
              {{ getCustomerName(selectedOrder) }}
            </el-descriptions-item>
            <el-descriptions-item label="Số điện thoại">
              {{ getCustomerPhone(selectedOrder) }}
            </el-descriptions-item>
            <el-descriptions-item label="Phương thức">
              {{ getPaymentMethodText(selectedOrder.payment_method) }}
            </el-descriptions-item>
          </el-descriptions>

          <!-- Per-ticket answers list if multiple tickets exist -->
          <div v-if="selectedOrder.tickets && selectedOrder.tickets.length" class="tickets-list-box mb-3">
            <h5 class="font-weight-bold mb-2 text-primary">Thông tin đăng ký theo vé:</h5>
            <div
              v-for="(t, idx) in selectedOrder.tickets"
              :key="idx"
              class="ticket-card-item p-3 mb-2"
            >
              <div class="font-weight-bold text-dark mb-1">
                <i class="fas fa-ticket-alt text-warning mr-1" /> Vé {{ idx + 1 }}
              </div>

              <!-- Custom answers per ticket -->
              <div v-if="hasCustomAnswers(t)" class="custom-answers-wrap">
                <div v-for="(val, key) in getCustomAnswers(t)" :key="key" class="font-size-xs text-dark mb-1">
                  • <strong>{{ formatKeyLabel(key) }}:</strong> {{ val }}
                </div>
              </div>
            </div>
          </div>

          <!-- Total Payment -->
          <div class="total-box text-right mt-3 pt-3 border-top">
            <span class="text-muted font-size-sm">Tổng tiền đã thanh toán:</span>
            <div class="text-danger font-weight-bold font-size-lg">
              {{ formatCurrency(selectedOrder.total_price) }}
            </div>
          </div>

          <!-- Simulated Barcode / QR -->
          <div class="ticket-barcode-wrap mt-4 text-center">
            <i class="fas fa-barcode barcode-icon" />
            <p class="font-size-xs text-muted mt-1">Mã vé điện tử dùng để check-in tại cổng</p>
          </div>
        </div>
      </div>

      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">Đóng</el-button>
        <el-button type="primary" icon="el-icon-printer" @click="handlePrintTicket">
          In vé / Tải vé
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'BookingOrdersPublic',
  data() {
    return {
      orders: [],
      searchKey: '',
      dialogVisible: false,
      selectedOrder: null,
      autoRefreshInterval: null
    }
  },
  computed: {
    filteredOrders() {
      if (!this.searchKey) return this.orders
      const kw = this.searchKey.toLowerCase().trim()
      return this.orders.filter((o) => {
        const codeMatch = (o.code || o.id || '').toLowerCase().includes(kw)
        const phoneMatch = o.customer && o.customer.phone && o.customer.phone.includes(kw)
        const nameMatch = o.customer && o.customer.full_name && o.customer.full_name.toLowerCase().includes(kw)
        const placeMatch = o.place_name && o.place_name.toLowerCase().includes(kw)
        return codeMatch || phoneMatch || nameMatch || placeMatch
      })
    }
  },
  mounted() {
    this.loadOrders()
    this.autoRefreshInterval = setInterval(() => {
      this.loadOrders()
    }, 5000)
  },
  beforeDestroy() {
    if (this.autoRefreshInterval) clearInterval(this.autoRefreshInterval)
  },
  methods: {
    formatDate(dateStr) {
      if (!dateStr) return ''
      const parts = dateStr.split('-')
      if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`
      return dateStr
    },
    formatCurrency(val) {
      if (val === null || val === undefined || isNaN(val)) return '0 VNĐ'
      return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(val)
    },
    handleGoHome() {
      this.$router.push('/booking')
    },
    handleGoMyOrders() {
      this.loadOrders()
    },

    loadOrders() {
      try {
        const stored = localStorage.getItem('booking_orders_fake_db')
        if (stored) {
          const orders = JSON.parse(stored)
          const now = Date.now()
          let changed = false

          // Auto expire pending orders > 15 minutes
          orders.forEach((o) => {
            if (o.status === 'PENDING') {
              const expireTime = o.expires_at || (o.created_at_timestamp ? o.created_at_timestamp + 15 * 60 * 1000 : 0)
              if (expireTime && now > expireTime) {
                o.status = 'CANCELLED'
                changed = true
              }
            }
          })

          if (changed) {
            localStorage.setItem('booking_orders_fake_db', JSON.stringify(orders))
          }

          this.orders = orders
        } else {
          this.orders = []
        }
      } catch (e) {
        console.error('Error reading booking_orders_fake_db:', e)
        this.orders = []
      }
    },

    getRemainingMinutes(order) {
      if (!order || order.status !== 'PENDING') return '0 phút'
      const expireTime = order.expires_at || (order.created_at_timestamp ? order.created_at_timestamp + 15 * 60 * 1000 : Date.now())
      const diffMs = expireTime - Date.now()
      if (diffMs <= 0) return 'Đã hết hạn'
      const m = Math.floor(diffMs / 60000)
      const s = Math.floor((diffMs % 60000) / 1000)
      return `${m} phút ${s}s`
    },

    getStatusTagType(status) {
      if (status === 'PAID') return 'success'
      if (status === 'PENDING') return 'warning'
      if (status === 'CANCELLED') return 'info'
      return 'secondary'
    },

    getStatusText(status) {
      if (status === 'PAID') return 'ĐÃ THANH TOÁN'
      if (status === 'PENDING') return 'ĐANG CHỜ (HOLDING)'
      if (status === 'CANCELLED') return 'ĐÃ HỦY'
      return status || 'CHỜ XỬ LÝ'
    },

    getOrderImage(order) {
      if (order.place_image) return order.place_image
      return 'https://images.unsplash.com/photo-1596422846543-75c6fc197f07?auto=format&fit=crop&w=800&q=80'
    },
    getCustomerName(order) {
      return (order.customer && order.customer.full_name) || 'Khách hàng'
    },
    getCustomerPhone(order) {
      return (order.customer && order.customer.phone) || '-'
    },
    getCustomerEmail(order) {
      return (order.customer && order.customer.email) || '-'
    },
    getPaymentMethodText(method) {
      if (method === 'momo') return 'Ví MoMo'
      if (method === 'vnpay') return 'VNPAY QR'
      if (method === 'card') return 'Thẻ Quốc tế / ATM'
      if (method === 'bank') return 'Chuyển khoản Ngân hàng'
      return method || 'Trực tuyến'
    },

    handleResumeBooking(order) {
      const placeId = order.place_id
      this.$router.push(`/booking/${placeId}?order_id=${order.id}`)
    },

    handleCancelOrder(order) {
      this.$confirm('Bạn có chắc chắn muốn hủy đơn hàng này? Số lượng vé sẽ được trả lại hệ thống.', 'Xác nhận hủy đơn hàng', {
        type: 'warning',
        confirmButtonText: 'Đồng ý hủy',
        cancelButtonText: 'Không'
      }).then(() => {
        try {
          const stored = localStorage.getItem('booking_orders_fake_db')
          if (stored) {
            const orders = JSON.parse(stored)
            const idx = orders.findIndex((o) => String(o.id) === String(order.id))
            if (idx !== -1) {
              orders[idx].status = 'CANCELLED'
              localStorage.setItem('booking_orders_fake_db', JSON.stringify(orders))
              this.$message.success('Hủy đơn hàng thành công!')
              this.loadOrders()
            }
          }
        } catch (e) {
          console.error(e)
        }
      }).catch(() => {})
    },

    handleOpenDetail(order) {
      this.selectedOrder = order
      this.dialogVisible = true
    },

    handlePrintTicket() {
      this.$message.success('Đã gửi lệnh in vé điện tử!')
    },

    hasCustomAnswers(obj) {
      if (!obj) return false
      return Object.keys(obj).some((k) => k.startsWith('custom_'))
    },

    getCustomAnswers(obj) {
      if (!obj) return {}
      const res = {}
      Object.keys(obj).forEach((k) => {
        if (k.startsWith('custom_')) {
          res[k] = obj[k]
        }
      })
      return res
    },

    formatKeyLabel(key) {
      const cleanKey = key.replace('custom_', '')
      if (this.selectedOrder && this.selectedOrder.question_labels && this.selectedOrder.question_labels[cleanKey]) {
        return this.selectedOrder.question_labels[cleanKey]
      }
      return cleanKey.replace(/_/g, ' ').toUpperCase()
    }
  }
}
</script>

<style lang="scss" scoped>
.booking-orders-public-page {
  min-height: 100vh;
  background-color: #f5f7fa;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
  }
}

// Header
.booking-header {
  background: #ffffff;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  position: sticky;
  top: 0;
  z-index: 100;

  .header-container {
    height: 70px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .brand-logo {
    display: flex;
    align-items: center;
    cursor: pointer;

    .logo-icon {
      width: 44px;
      height: 44px;
      background: linear-gradient(135deg, #1890ff 0%, #096dd9 100%);
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #ffffff;
      font-size: 20px;
      margin-right: 12px;
    }

    .brand-title {
      font-size: 20px;
      font-weight: 700;
      color: #1f2937;
      margin: 0;
      line-height: 1.2;
    }

    .brand-subtitle {
      font-size: 11px;
      color: #6b7280;
    }
  }

  .header-nav {
    display: flex;
    align-items: center;
    gap: 12px;

    .my-orders-btn {
      font-weight: 600;
      padding: 9px 20px;
    }
  }
}

// Main Content
.main-content {
  padding: 75px 20px 60px 20px;

  .page-title-wrap {
    margin-bottom: 28px;
    .title-flex {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    .page-title {
      font-size: 24px;
      font-weight: 800;
      color: #111827;
      margin: 0;
    }

    .page-desc {
      font-size: 14px;
      color: #6b7280;
      margin-top: 4px;
    }
  }
}

// Filter Card
.filter-card {
  background: #ffffff;
  border-radius: 12px;
  padding: 16px 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  display: flex;
  align-items: center;

  .total-badge {
    font-size: 14px;
    color: #4b5563;
    line-height: 36px;
  }
}

// Order Cards
.order-card {
  border-radius: 14px;
  overflow: hidden;
  border: none;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08) !important;
  }

  .order-card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 12px;
    border-bottom: 1px solid #f3f4f6;

    .order-code {
      font-size: 15px;
      color: #1f2937;
    }
  }

  .place-preview-flex {
    display: flex;
    gap: 14px;
    margin-top: 12px;

    .place-thumb {
      width: 90px;
      height: 70px;
      object-fit: cover;
      border-radius: 8px;
    }

    .place-info {
      flex: 1;

      .place-name {
        font-size: 16px;
        font-weight: 700;
        color: #111827;
        margin: 0 0 6px 0;
      }

      .order-date, .order-time {
        font-size: 12px;
        color: #6b7280;
        margin: 0 0 2px 0;
      }
    }
  }

  .order-details-summary {
    .detail-label {
      font-size: 11px;
      color: #9ca3af;
      text-transform: uppercase;
    }

    .detail-val {
      font-size: 13px;
      color: #374151;
    }
  }

  .order-card-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 14px;
    padding-top: 12px;
    border-top: 1px dashed #e5e7eb;

    .pay-method {
      font-size: 12px;
      color: #6b7280;
      font-weight: 600;
    }

    .footer-btns {
      display: flex;
      gap: 6px;
    }
  }
}

.ticket-header-box {
  background: linear-gradient(135deg, #1890ff 0%, #096dd9 100%);
  color: #ffffff;
  padding: 20px;
  text-align: center;
  border-radius: 8px 8px 0 0;

  .ticket-brand {
    font-size: 12px;
    letter-spacing: 2px;
    opacity: 0.8;
  }

  .ticket-code {
    font-size: 24px;
    font-weight: 800;
    letter-spacing: 1px;
    margin-top: 4px;
  }
}

.ticket-card-item {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.ticket-barcode-wrap {
  .barcode-icon {
    font-size: 70px;
    color: #1e293b;
  }
}

.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.font-size-sm {
  font-size: 12px;
}
.font-size-xs {
  font-size: 11px;
}
.font-size-lg {
  font-size: 20px;
}
</style>
